/**
 * How many page loads and navigations become Sentry traces, and how many of those get a
 * profile. Production keeps a share; every other environment (alpha, beta, local) keeps
 * them all, since their traffic is small and they are where a change is first looked at.
 *
 * The browser makes the sampling decision for the whole trace: the API continues it
 * (`tracePropagationTargets` in `sentry.ts`), so these rates also set how many portal
 * requests the API traces. See `API/src/utilities/sentry_sampling.py`.
 */

export const PRODUCTION_TRACES_SAMPLE_RATE = 0.2;
export const PRODUCTION_PROFILES_SAMPLE_RATE = 0.25; // of the traces kept, so 5% of pages

/**
 * Job submission is the flow customers most need to work, and it is rare next to
 * dashboard browsing, so every visit to it stays traced in production too.
 */
const ALWAYS_TRACED_ROUTES = ["/jobs/submit"];

/** The parts of Sentry's `TracesSamplerSamplingContext` the sampler reads. */
interface TracesSamplingContext {
	name: string;
	inheritOrSampleWith: (fallbackSampleRate: number) => number;
}

export function makeTracesSampler(isProduction: boolean): (context: TracesSamplingContext) => number {
	if (!isProduction) return () => 1.0;
	return ({ name, inheritOrSampleWith }) =>
		ALWAYS_TRACED_ROUTES.some((route) => name.startsWith(route))
			? 1.0
			: inheritOrSampleWith(PRODUCTION_TRACES_SAMPLE_RATE);
}

export function profilesSampleRate(isProduction: boolean): number {
	return isProduction ? PRODUCTION_PROFILES_SAMPLE_RATE : 1.0;
}
